<template>
  <div class="app-body" :class="{ mobile: isMobile }">
    <nav-bar
      v-if="!isOnHomepage"
      :narrowWidth="!oidcIsAuthenticated"
      :isShowSidebar="isShowSidebar"
    ></nav-bar>
    <div
      id="main-wrapper"
      class="navigation"
      :class="{ toggled: oidcIsAuthenticated && showSideBar }"
    >
      <div
        v-show="isShowSidebar"
        class="sidebar"
        :class="{ compact: !isExpandSidebar }"
      >
        <SideBar
          @onToggleIsShowSidebarOnMobile="toggleIsShowSidebarOnMobile"
        ></SideBar>
      </div>
      <!-- Page Content -->
      <main v-if="!isHideMainOnMobile" id="page-content-wrapper">
        <router-view />
      </main>
    </div>
    <Footer v-if="!oidcIsAuthenticated && !isOnHomepage"></Footer>
    <InfoBanner
      v-if="isShowErrorInfoBanner"
      infoType="danger"
      :infoText="errorInfoBannerText"
      @onClose="setIsShowErrorInfoBanner(false)"
    ></InfoBanner>
    <template v-if="$router.currentRoute.name === 'suspicious-traffic'">
      <welcome-tour></welcome-tour>
      <report-tour></report-tour>
    </template>
    <template v-if="isShowSessionExpiredModal">
      <SessionExpiredModal></SessionExpiredModal>
    </template>

    <Spinner :fullscreen="true" v-if="isCheckingAuthentication">
      <template v-slot:header
        >Checking authentication</template
      >
      <template #default>
        We are checking your authentication.<br />This may take a few seconds.
      </template>
    </Spinner>
  </div>
</template>

<script>
import NavBar from './components/ui/NavBar.vue';
import SideBar from './components/ui/SideBar.vue';
import Footer from './components/ui/Footer.vue';
import WelcomeTour from './components/ui/WelcomeTour.vue';
import ReportTour from './components/ui/ReportTour.vue';
import mixin from './utils/mixin';
import SessionExpiredModal from './components/SessionExpiredModal.vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import InfoBanner from './components/ui/InfoBanner.vue';
import Spinner from './components/ui/Spinner.vue';

export default {
  mixins: [mixin],
  components: {
    NavBar,
    SideBar,
    Footer,
    WelcomeTour,
    ReportTour,
    SessionExpiredModal,
    InfoBanner,
    Spinner
  },
  data() {
    return {
      isOnHomepage: false,
      isOnWelcomePage: false,
      timer: 0
    };
  },
  computed: {
    ...mapState([
      'isShowSessionExpiredModal',
      'isShowErrorInfoBanner',
      'errorInfoBannerText',
      'isCheckingAuthentication'
    ]),

    isShowSidebar() {
      if (this.isOnHomepage || this.isOnWelcomePage) return false;

      if (!this.isMobile) return true;

      return this.isShowSidebarOnMobile;
    },

    isHideMainOnMobile() {
      if (!this.isMobile || this.isOnHomepage) return false;

      return this.isShowSidebarOnMobile;
    }
  },
  mounted() {
    this.authenticateSilent();

    window.addEventListener('resize', this.handleSideBar);
    this.handleSideBar();

    // DO NOT request theme data list in report mode due to CORS issue breaking recurring report
    if (!process.env.VUE_APP_REPORT_MODE) {
      this.fetchThemeDataList();
    }
  },
  methods: {
    ...mapMutations(['setIsShowErrorInfoBanner']),
    ...mapActions('authStore', ['authenticateOidcSilent']),
    ...mapActions('dataStore', ['fetchThemeDataList']),

    handleSideBar() {
      const width = window.innerWidth;
      this.updateScreenWidth(width);

      if (!this.oidcIsAuthenticated) {
        const element = document.getElementById('wrapper');
        if (element) {
          element.classList.add('hide-sidebar');
        }
        return;
      }
      if (
        (width < 768 && this.showSideBar) ||
        (width > 768 && !this.showSideBar)
      ) {
        this.toggleSideBar();
      }
    },

    toggleIsExpandSidebar() {
      setIsExpandSidebar(!this.isExpandSidebar);
    },

    toggleIsShowSidebarOnMobile() {
      this.setIsShowSidebarOnMobile(!this.isShowSidebarOnMobile);
    },

    authenticateSilent() {
      this.authenticateOidcSilent()
        .then(() => {})
        .catch(err => {});
    },

    hideUsabillaIcon() {
      const element = document.querySelector('.usabilla_live_button_container');

      if (element) element.setAttribute('style', 'right: -100vw !important');
    },

    restoreUsabillaIcon() {
      const element = document.querySelector('.usabilla_live_button_container');

      if (element) {
        element.setAttribute('style', 'right: 0 !important');
      } else {
        setTimeout(() => {
          this.restoreUsabillaIcon();
        }, 1000);
      }
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        if (['/', '/auth-callback'].includes(to.path)) {
          this.isOnHomepage = true;
        } else {
          this.isOnHomepage = false;
        }
        if (['/welcome'].includes(to.path)) {
          this.isOnWelcomePage = true;
        } else {
          this.isOnWelcomePage = false;
        }

        // Show usabilla Feedback icon when it's not welcome, auth-callback or home page
        if (!['/', '/auth-callback', '/welcome'].includes(to.path)) {
          this.restoreUsabillaIcon();
        } else {
          this.hideUsabillaIcon();
        }
      }
    }
  }
};
</script>

<style lang="scss">
.navigation {
  position: relative;
  top: 40px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;

  .sidebar {
    flex: 0 0 300px;
    transition: flex 0.5s;

    &.compact {
      flex: 0 0 64px;
    }
  }
}

.v-tour__target--highlighted {
  box-shadow: 0 0 0 99999px rgba(0, 0, 0, 0.4) !important;
}

.spinner.ivu-spin-large {
  z-index: 999;
  .ivu-spin-dot {
    display: block;
    width: 60px;
    height: 60px;
    margin: 0 auto;
  }
}

#wrapper.navigation {
  padding-left: 0 !important;
}

.hide-sidebar#wrapper {
  padding-left: 0 !important;
}

main {
  // border: 1px red solid;
}

main#page-content-wrapper {
  flex: 1;
  position: relative;
  width: 100%;
  min-height: calc(100vh - 119px);

  & > .ivu-spin {
    z-index: 99;

    .ivu-spin-main {
      top: 25%;
    }

    .ivu-spin-dot {
      width: 100px;
      height: 100px;
    }
  }

  .container.py-4 {
    padding-top: 0 !important;
  }

  .btn-primary {
    background-color: $apnic-blue;
  }

  @media (max-width: 526px) {
    min-height: calc(100vh - 168px);
  }
}

.mobile-modal {
  .ivu-modal {
    width: 300px !important;
    margin: 0 auto;
  }
  .ivu-modal-header {
    border-bottom: none;
  }
  .ivu-modal-footer {
    border-top: none;
  }

  h1 {
    margin-bottom: 0;
    font-size: 30px;
  }

  p {
    font-size: 1rem;
  }

  .btn.btn-close {
    width: 71px;
    min-width: 71px;
  }
}

button:disabled {
  cursor: not-allowed;
}

.app-body {
  margin-bottom: 0 !important;
  padding: 0 !important;
}

@media screen and (-webkit-min-device-pixel-ratio: 0) {
  select,
  textarea,
  input {
    font-size: 16px;
  }
}

.prefix-icon {
  position: relative;
  padding-left: 1.2rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
  }
}

.prefix-icon--table {
  padding-left: 0;

  &::before {
    left: 0.4rem;
  }

  .vxe-cell--label {
    padding-left: 0.8rem;
    color: $text-black;
  }
}

.vxe-cell--title {
  color: $text-black;
}

// Change the default style of filter dropdown
.vxe-table--filter-wrapper {
  height: 9rem;

  .vxe-table--filter-body {
    overflow: visible;
  }
}

.vxe-body--row .word-break--normal .vxe-cell {
  word-break: normal;
}

.green-dot::before {
  background-color: #208836;
}
.yellow-dot::before {
  background-color: #ffc700;
}
.red-dot::before {
  background-color: #b00020;
}
.green-circle::before {
  border: 1px solid #208836;
}
.yellow-circle::before {
  border: 1px solid #ffc700;
}
.red-circle::before {
  border: 1px solid #b00020;
}

.with-asterisk::after {
  content: ' *';
  color: $warning-red;
}

.mobile {
  .navigation {
    top: 0;
    // z-index: 1;
  }
}

.truncated {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.routing-status-download-tooltip .tooltip-inner {
  max-width: 10rem;
}

.roa-info__tooltip .tooltip-inner {
  min-width: 14rem;
}

.usabilla_live_button_container {
  right: -100vw !important;
}

.page-title {
  margin: 0;
  margin-bottom: 1.5rem;
  line-height: 2rem;
  color: $text-blue;
  font-size: 1.75rem;
}
.mobile .page-title {
  font-size: 1.25rem;
}
</style>
